import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet } from '../../utils/api'
import { fmtDuration } from '../chat-history/ChatHistoryCompaniesView'

interface Company {
  alias: string
  company_name: string
  company_logo_url?: string | null
  total_chats: number
  total_time_seconds: number
  avg_resolution_seconds: number | null
}

/** Analytics-dashboard card: companies ranked by time on chat, as a table.
 *  Replaces the old "Claude AI Suggestions" card. */
export const MostActiveChats: React.FC = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    apiGet<{ companies: Company[] }>('/chat-history/companies?sort=most_time')
      .then((d) => {
        if (active) setRows((d.companies || []).slice(0, 8))
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  return (
    <div className="p-5 rounded-xl bg-surface border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-txt-primary">Most Active Chats</h3>
        </div>
        <button onClick={() => navigate('/chat-history')} className="text-xs text-brand-600 hover:underline">
          View all
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-txt-tertiary">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-txt-tertiary">No chats yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                <th className="py-2 pr-2 font-medium">Company</th>
                <th className="py-2 px-2 font-medium text-right">Chats</th>
                <th className="py-2 px-2 font-medium text-right">Time</th>
                <th className="py-2 pl-2 font-medium text-right">Avg resolve</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.alias}
                  onClick={() => navigate(`/chat-history/${encodeURIComponent(c.alias)}`)}
                  className="border-b border-border/60 last:border-0 cursor-pointer hover:bg-surface-hover"
                >
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {c.company_logo_url ? (
                        <img
                          src={c.company_logo_url}
                          alt=""
                          className="w-6 h-6 rounded object-contain bg-white border border-border shrink-0"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                        />
                      ) : (
                        <div className="w-6 h-6 rounded bg-brand-100 text-brand-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                          {(c.company_name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="truncate text-txt-primary font-medium" title={c.company_name}>
                        {c.company_name}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right text-txt-primary tabular-nums">{c.total_chats}</td>
                  <td className="py-2 px-2 text-right text-txt-primary tabular-nums">{fmtDuration(c.total_time_seconds)}</td>
                  <td className="py-2 pl-2 text-right text-txt-secondary tabular-nums">{fmtDuration(c.avg_resolution_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
